import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';
import {
  PieChart, Pie, Cell,
  BarChart, Bar,
  LineChart, Line,
  XAxis, YAxis,
  Tooltip, ResponsiveContainer,
  Legend, CartesianGrid
} from 'recharts';

const API_BASE_URL = 'http://72.61.235.16:5001/api/v1';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalProjects: 0,
    activeProjects: 0,
    completedProjects: 0,
    totalUsers: 0,
    pendingPayments: 0,
    reportSubmittedProjects: [],
    projectsWithQuotations: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const requests = [
        axios.get(`${API_BASE_URL}/projects`, config).catch(() => ({ data: { data: { projects: [] } } })),
      ];

      if (user?.role === 'admin' || user?.role === 'super_admin') {
        requests.push(axios.get(`${API_BASE_URL}/users`, config).catch(() => ({ data: { data: { users: [] } } })));
      }

      const responses = await Promise.all(requests);

      const projects = responses[0].data.data.projects || [];
      const users = responses[1]?.data.data.users || [];

      // Filter projects based on role
      let userProjects = projects;
      if (user?.role === 'finance') {
        userProjects = projects.filter(p => p.finance_id === user.id);
      } else if (user?.role === 'project_manager') {
        userProjects = projects.filter(p => p.pm_id === user.id);
      } else if (user?.role === 'customer') {
        userProjects = projects.filter(p => p.customer_id === user.id);
      }

      // Calculate stats
      const reportSubmittedProjects = userProjects.filter(p => p.status === 'REPORT_SUBMITTED');
      const projectsWithQuotations = userProjects.filter(p => 
        ['QUOTATION_GENERATED', 'CUSTOMER_APPROVED', 'ADVANCE_PENDING', 'ADVANCE_PAID', 'WORK_STARTED', 'COMPLETED', 'CLOSED'].includes(p.status)
      );

      // Count pending payments for finance
      let pendingPayments = 0;
      if (user?.role === 'finance') {
        for (const project of userProjects) {
          try {
            const paymentResponse = await axios.get(`${API_BASE_URL}/payments/project/${project.id}`, config);
            const payments = paymentResponse.data.data.payments || [];
            pendingPayments += payments.filter(p => p.status === 'pending').length;
          } catch (err) {
            // Skip if error
          }
        }
      }

      setStats({
        totalProjects: userProjects.length,
        activeProjects: userProjects.filter(p => !['CLOSED', 'COMPLETED'].includes(p.status)).length,
        completedProjects: userProjects.filter(p => p.status === 'COMPLETED' || p.status === 'CLOSED').length,
        totalUsers: users.length,
        pendingPayments,
        reportSubmittedProjects,
        projectsWithQuotations,
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateQuotation = (project) => {
    navigate('/quotations', { state: { generateFor: project } });
  };

  const getStatusColor = (status) => {
    const statusColors = {
      REPORT_SUBMITTED: '#f59e0b',
      QUOTATION_GENERATED: '#3b82f6',
      CUSTOMER_APPROVED: '#10b981',
      ADVANCE_PENDING: '#ef4444',
      ADVANCE_PAID: '#22c55e',
      WORK_STARTED: '#3b82f6',
      COMPLETED: '#10b981',
      CLOSED: '#64748b',
    };
    return statusColors[status] || '#757575';
  };

  const getBackgroundImage = () => {
    const role = user?.role;
    if (role === 'admin' ) {
      return '/admin-background.png';
    } else if (role === 'super_admin') {
      return '/super-background.png';
    }else if (role === 'finance') {
      return '/finance-background.png';
    } else if (role === 'project_manager') {
      return '/pm-background.png';
    } else if (role === 'customer') {
      return '/customer-background.png';
    }
    return '/background.png';
  };

  const getRoleDisplayName = () => {
    const roleNames = {
      super_admin: 'Super Administrator',
      admin: 'Administrator',
      project_manager: 'Project Manager',
      site_incharge: 'Site Incharge',
      finance: 'Finance Manager',
      customer: 'Customer'
    };
    return roleNames[user?.role] || user?.role?.replace('_', ' ');
  };

/* ===== STATUS DISTRIBUTION ===== */
const statusData = [
  { name: 'Active', value: stats.activeProjects },
  { name: 'Completed', value: stats.completedProjects },
];

const STATUS_COLORS = ['#6366F1', '#10B981'];

/* ===== PROJECT OVERVIEW ===== */
const overviewData = [
  {
    name: 'Projects',
    Total: stats.totalProjects,
    Active: stats.activeProjects,
    Completed: stats.completedProjects,
  }
];

/* ===== FINANCE DATA ===== */
const financeData = [
  {
    name: 'Finance',
    Pending: stats.pendingPayments,
    Quotations: stats.projectsWithQuotations.length,
    Reports: stats.reportSubmittedProjects.length,
  }
];

/* ===== MOCK MONTHLY TREND (can replace with API later) ===== */
const monthlyTrend = [
  { month: 'Jan', projects: 4 },
  { month: 'Feb', projects: 6 },
  { month: 'Mar', projects: 8 },
  { month: 'Apr', projects: 5 },
  { month: 'May', projects: 9 },
];

  return (
    <div className="dashboard-page">
      {/* Hero Section with Background Image */}
     <div 
  className="hero-section-modern"
  style={{ backgroundImage: `url(${getBackgroundImage()})` }}
>
  <div className="hero-overlay"></div>

  <div className="hero-inner">

    {/* LEFT CONTENT */}
    <div className="hero-left">
      <div className="hero-greeting">
        <span className="wave">👋</span>
        <span>Welcome Back</span>
      </div>

      <h1 className="hero-username-modern">
        {user?.name}
      </h1>

      <div className="hero-role-badge">
        {getRoleDisplayName()}
      </div>

      <p className="hero-subtext">
        Here's what's happening with your projects today.
      </p>
    </div>

    {/* RIGHT LOGO */}
    <div className="hero-right">
      <div className="logo-card">
        <img src="/logo.png" alt="Logo" />
      </div>
    </div>

  </div>
</div>

      {/* Dashboard Content */}
      <div className="dashboard-content">
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading dashboard...</p>
          </div>
        ) : (
          <div className="dashboard-sections">
            {/* ===== SUPER ADMIN DASHBOARD ===== */}
{user?.role === 'super_admin' && (
  <>
    <div className="dashboard-section">
      <h2 className="section-title modern-title">
        Super Admin Control Center
      </h2>

      <div className="modern-grid">

        <div 
          className="modern-card"
          onClick={() => navigate('/create-admin')}
        >
          <div className="modern-icon blue">
            <i className="fas fa-user-plus"></i>
          </div>
          <div className="modern-card-content">
            <h3>Add New Admin</h3>
            <p>Create and manage administrator accounts</p>
          </div>
        </div>

        <div 
          className="modern-card"
          onClick={() => navigate('/create-super-admin')}
        >
          <div className="modern-icon purple">
            <i className="fas fa-user-shield"></i>
          </div>
          <div className="modern-card-content">
            <h3>Add Super Admin</h3>
            <p>Grant elevated system permissions</p>
          </div>
        </div>

        <div 
          className="modern-card"
          onClick={() => navigate('/audit-logs')}
        >
          <div className="modern-icon orange">
            <i className="fas fa-clipboard-list"></i>
          </div>
          <div className="modern-card-content">
            <h3>Audit Logs</h3>
            <p>Monitor system-wide activities</p>
          </div>
        </div>

        <div 
          className="modern-card"
          onClick={() => navigate('/audit-logs', { state: { role: 'admin' } })}
        >
          <div className="modern-icon green">
            <i className="fas fa-user-tie"></i>
          </div>
          <div className="modern-card-content">
            <h3>Admin Activities</h3>
            <p>Track administrator actions</p>
          </div>
        </div>

      </div>
    </div>
  </>
)}

 {/* Admin Dashboard */}
            {user?.role === 'admin' && (
  <div className="saas-dashboard">

    {/* KPI SUMMARY ROW */}
    <div className="saas-kpi-row">

      <div className="saas-kpi-card">
        <div className="kpi-header">
          <span>Total Projects</span>
          <i className="fas fa-clipboard-list"></i>
        </div>
        <h2>{stats.totalProjects}</h2>
        <p className="kpi-trend positive">↑ 12% this month</p>
      </div>

      <div className="saas-kpi-card">
        <div className="kpi-header">
          <span>Active Projects</span>
          <i className="fas fa-play-circle"></i>
        </div>
        <h2>{stats.activeProjects}</h2>
        <p className="kpi-trend neutral">Ongoing work</p>
      </div>

      <div className="saas-kpi-card">
        <div className="kpi-header">
          <span>Completed</span>
          <i className="fas fa-check-circle"></i>
        </div>
        <h2>{stats.completedProjects}</h2>
        <p className="kpi-trend positive">↑ Efficiency improved</p>
      </div>

      <div className="saas-kpi-card">
        <div className="kpi-header">
          <span>Total Users</span>
          <i className="fas fa-users"></i>
        </div>
        <h2>{stats.totalUsers}</h2>
        <p className="kpi-trend neutral">Active members</p>
      </div>

    </div>

    {/* ACTION PANEL */}
    <div className="saas-action-panel">
      <div className="panel-left">
        <h3>Quick Actions</h3>
        <p>Manage your system efficiently</p>
      </div>

      <div className="panel-actions">
        <button onClick={() => navigate('/projects')}>
          + Create Project
        </button>
        <button onClick={() => navigate('/users')}>
          Manage Users
        </button>
        <button onClick={() => navigate('/reports')}>
          View Reports
        </button>
      </div>
    </div>

  </div>
)}

            {/* ===== PRO ANALYTICS SECTION ===== */}
<div className="pro-analytics-section">
  <h2 className="section-title">Advanced Analytics</h2>

  <div className="pro-charts-grid">

    {/* Status Pie */}
    <div className="pro-chart-card glass-card">
      <h3>Status Distribution</h3>
      <ResponsiveContainer width="100%" height={280}>
        <PieChart>
          <Pie
            data={statusData}
            dataKey="value"
            outerRadius={90}
            label
          >
            {statusData.map((entry, index) => (
              <Cell key={index} fill={STATUS_COLORS[index]} />
            ))}
          </Pie>
          <Tooltip />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </div>

    {/* Overview Bar */}
    <div className="pro-chart-card glass-card">
      <h3>Project Overview</h3>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={overviewData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Bar dataKey="Total" fill="#6366F1" radius={[8,8,0,0]} />
          <Bar dataKey="Active" fill="#3B82F6" radius={[8,8,0,0]} />
          <Bar dataKey="Completed" fill="#10B981" radius={[8,8,0,0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>

    {/* Monthly Trend */}
    <div className="pro-chart-card glass-card">
      <h3>Monthly Trend</h3>
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={monthlyTrend}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" />
          <YAxis />
          <Tooltip />
          <Line 
            type="monotone"
            dataKey="projects"
            stroke="#6366F1"
            strokeWidth={3}
            dot={{ r: 5 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>

    {/* Finance Chart */}
    {user?.role === 'finance' && (
      <div className="pro-chart-card glass-card">
        <h3>Finance Overview</h3>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={financeData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="Pending" fill="#EF4444" radius={[8,8,0,0]} />
            <Bar dataKey="Quotations" fill="#3B82F6" radius={[8,8,0,0]} />
            <Bar dataKey="Reports" fill="#F59E0B" radius={[8,8,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    )}

  </div>
</div>

           

           {/* ===== FINANCE SaaS DASHBOARD ===== */}
{user?.role === 'finance' && (
  <div className="finance-saas-dashboard">

    {/* KPI SUMMARY */}
    <div className="finance-kpi-row">

      <div 
        className="finance-kpi-card"
        onClick={() => navigate('/projects')}
      >
        <div className="finance-kpi-header">
          <span>Total Projects</span>
          <i className="fas fa-clipboard-list"></i>
        </div>
        <h2>{stats.totalProjects}</h2>
        <p className="finance-subtext">Managed by you</p>
      </div>

      <div 
        className="finance-kpi-card"
        onClick={() => navigate('/payments')}
      >
        <div className="finance-kpi-header">
          <span>Pending Verifications</span>
          <i className="fas fa-clock"></i>
        </div>
        <h2>{stats.pendingPayments}</h2>
        <p className="finance-danger">Requires attention</p>
      </div>

      <div 
        className="finance-kpi-card"
        onClick={() => navigate('/quotations')}
      >
        <div className="finance-kpi-header">
          <span>Quotations Generated</span>
          <i className="fas fa-file-invoice-dollar"></i>
        </div>
        <h2>{stats.projectsWithQuotations.length}</h2>
        <p className="finance-positive">Revenue pipeline</p>
      </div>

      <div 
        className="finance-kpi-card"
        onClick={() => navigate('/quotations')}
      >
        <div className="finance-kpi-header">
          <span>Pending Quotations</span>
          <i className="fas fa-file-alt"></i>
        </div>
        <h2>{stats.reportSubmittedProjects.length}</h2>
        <p className="finance-warning">Awaiting generation</p>
      </div>

    </div>

    {/* QUICK ACTION STRIP */}
    <div className="finance-action-strip">
      <div>
        <h3>Finance Control Center</h3>
        <p>Manage quotations, payments & billing workflows</p>
      </div>
      <div className="finance-actions">
        <button onClick={() => navigate('/payments')}>
          Verify Payments
        </button>
        <button onClick={() => navigate('/quotations')}>
          Manage Quotations
        </button>
      </div>
    </div>

    {/* QUOTATION GENERATION */}
    {stats.reportSubmittedProjects.length > 0 && (
      <div className="finance-section">
        <h2>Generate Quotations</h2>
        <div className="finance-project-grid">
          {stats.reportSubmittedProjects.map((project) => (
            <div key={project.id} className="finance-project-card">
              <div className="finance-project-top">
                <div>
                  <h4>{project.name}</h4>
                  <p>{project.location}</p>
                </div>
                <span 
                  className="finance-status-badge"
                  style={{ backgroundColor: getStatusColor(project.status) }}
                >
                  REPORT SUBMITTED
                </span>
              </div>

              <button 
                className="finance-primary-btn"
                onClick={() => handleGenerateQuotation(project)}
              >
                Generate Quotation
              </button>
            </div>
          ))}
        </div>
      </div>
    )}

    {/* PROJECTS WITH QUOTATIONS */}
    {stats.projectsWithQuotations.length > 0 && (
      <div className="finance-section">
        <h2>Active Billing Projects</h2>
        <div className="finance-project-grid">
          {stats.projectsWithQuotations.map((project) => (
            <div key={project.id} className="finance-project-card">
              <div className="finance-project-top">
                <div>
                  <h4>{project.name}</h4>
                  <p>{project.location}</p>
                </div>
                <span 
                  className="finance-status-badge"
                  style={{ backgroundColor: getStatusColor(project.status) }}
                >
                  {project.status.replace(/_/g, ' ')}
                </span>
              </div>

              <div className="finance-btn-row">
                <button 
                  onClick={() => navigate(`/projects/${project.id}`)}
                >
                  View
                </button>

                {project.status !== 'CLOSED' && (
                  <button 
                    className="secondary"
                    onClick={() =>
                      navigate(`/projects/${project.id}/add-extra-charge`, { state: { project } })
                    }
                  >
                    + Extra Charge
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    )}

  </div>
)}

           {/* ===== PROJECT MANAGER SaaS DASHBOARD ===== */}
{user?.role === 'project_manager' && (
  <div className="pm-saas-dashboard">

    {/* KPI ROW */}
    <div className="pm-kpi-row">

      <div 
        className="pm-kpi-card"
        onClick={() => navigate('/projects')}
      >
        <div className="pm-kpi-header">
          <span>My Projects</span>
          <i className="fas fa-clipboard-list"></i>
        </div>
        <h2>{stats.totalProjects}</h2>
        <p className="pm-subtext">Assigned to you</p>
      </div>

      <div 
        className="pm-kpi-card"
        onClick={() => navigate('/projects')}
      >
        <div className="pm-kpi-header">
          <span>Active Projects</span>
          <i className="fas fa-play-circle"></i>
        </div>
        <h2>{stats.activeProjects}</h2>
        <p className="pm-warning">Currently running</p>
      </div>

      <div 
        className="pm-kpi-card"
        onClick={() => navigate('/projects')}
      >
        <div className="pm-kpi-header">
          <span>Completed</span>
          <i className="fas fa-check-circle"></i>
        </div>
        <h2>{stats.completedProjects}</h2>
        <p className="pm-positive">Successfully delivered</p>
      </div>

    </div>

    {/* ACTION STRIP */}
    <div className="pm-action-strip">
      <div>
        <h3>Project Operations</h3>
        <p>Track progress and manage execution efficiently</p>
      </div>

      <div className="pm-actions">
        <button onClick={() => navigate('/projects')}>
          View All Projects
        </button>
      </div>
    </div>

  </div>
)}
            {/* ===== CUSTOMER SaaS DASHBOARD ===== */}
{user?.role === 'customer' && (
  <div className="customer-saas-dashboard">

    {/* PROJECT SUMMARY */}
    <div className="customer-kpi-row">

      <div 
        className="customer-kpi-card"
        onClick={() => navigate('/projects')}
      >
        <div className="customer-kpi-header">
          <span>Total Projects</span>
          <i className="fas fa-clipboard-list"></i>
        </div>
        <h2>{stats.totalProjects}</h2>
        <p className="customer-subtext">Projects you've initiated</p>
      </div>

      <div 
        className="customer-kpi-card"
        onClick={() => navigate('/projects')}
      >
        <div className="customer-kpi-header">
          <span>Ongoing</span>
          <i className="fas fa-play-circle"></i>
        </div>
        <h2>{stats.activeProjects}</h2>
        <p className="customer-warning">Currently in progress</p>
      </div>

      <div 
        className="customer-kpi-card"
        onClick={() => navigate('/projects')}
      >
        <div className="customer-kpi-header">
          <span>Completed</span>
          <i className="fas fa-check-circle"></i>
        </div>
        <h2>{stats.completedProjects}</h2>
        <p className="customer-positive">Successfully delivered</p>
      </div>

    </div>

    {/* CLIENT ACTION PANEL */}
    <div className="customer-action-strip">
      <div>
        <h3>Your Project Portal</h3>
        <p>Track progress, manage payments, and download reports</p>
      </div>

      <div className="customer-actions">
        <button onClick={() => navigate('/quotations')}>
          View Quotations
        </button>

        <button onClick={() => navigate('/payments')}>
          Make Payment
        </button>

        <button onClick={() => navigate('/reports')}>
          View Reports
        </button>
      </div>
    </div>

  </div>
)}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;